import { Typography } from "@mui/material";
import { APP_CURRENCY_CODE } from "App";
import { BlFormInput } from "Components/Shared/Inputs/Form/BlFormInput";
import { Resources, useResource } from "Translations/Resources";
import { getCurrencySymbol } from "Utils/CurrencyUtils";
import BigNumber from "bignumber.js";
import { get } from "lodash-es";
import { Control, FieldErrors, FieldPath, FieldValues } from "react-hook-form";
import styled from "styled-components";

const PageResources = Resources.Settings.Services.PriceColumns;

const PRICE_COLUMN_WIDTH = 140;

/**
 * Grid wrapper shared by the services settings and the service package form.
 * The first column holds the variant label, the remaining three hold the
 * price inputs rendered by PriceColumnsHeader / PriceColumnsRow.
 */
export const StyledPriceGrid = styled.div<{ $labelColumnWidth: number }>`
  display: grid;
  grid-template-columns: ${props => props.$labelColumnWidth}px repeat(
      3,
      ${PRICE_COLUMN_WIDTH}px
    );
  column-gap: ${props => props.theme.spacing(1.5)};
  row-gap: ${props => props.theme.spacing(0.5)};
  /* the price inputs reserve room for a helper text, so the row height is
     driven by them - the checkbox only needs to line up with the input box */
  align-items: start;
  margin-top: ${props => props.theme.spacing(3)};

  .checkbox {
    margin: ${props => props.theme.spacing(1.5, 0, 0, 0)};
  }

  .input-wrapper {
    width: ${PRICE_COLUMN_WIDTH}px;
    input {
      text-align: right;
      padding-right: ${props => props.theme.spacing(4)};
    }
  }
`;

const StyledHeaderCell = styled.div`
  text-align: right;
  padding-bottom: ${props => props.theme.spacing(0.5)};
`;

const StyledPriceCell = styled.div<{ $hasError: boolean }>`
  .bl-text-input__unit {
    ${props => (props.$hasError ? "top: -18px !important" : "")}
  }
`;

const getTaxRatePercentage = (taxRate: number) =>
  new BigNumber(taxRate).times(100).decimalPlaces(2).toNumber();

type HeaderProps = {
  taxRate: number;
};

export const PriceColumnsHeader: React.FunctionComponent<HeaderProps> = ({
  taxRate,
}) => {
  const { t } = useResource();

  const columns = [
    {
      label: t(PageResources.WithoutTax.Label),
      hint: t(PageResources.WithoutTax.Hint),
    },
    {
      label: t(PageResources.WithTax.Label, {
        rate: getTaxRatePercentage(taxRate),
      }),
      hint: t(PageResources.WithTax.Hint),
    },
    {
      label: t(PageResources.NonVatPayer.Label),
      hint: t(PageResources.NonVatPayer.Hint),
    },
  ];

  return (
    <>
      <div />
      {columns.map(column => (
        <StyledHeaderCell key={column.label}>
          <Typography fontSize={13} fontWeight={700} textTransform="uppercase">
            {column.label}
          </Typography>
          <Typography variant="subtitle2" fontStyle={"normal"} fontSize={12}>
            {column.hint}
          </Typography>
        </StyledHeaderCell>
      ))}
    </>
  );
};

type RowProps<FormType extends FieldValues> = {
  control: Control<FormType, object>;
  errors: FieldErrors<FormType>;
  /** Field holding the price without tax. Bound by both the first and the third column. */
  netFieldName: FieldPath<FormType>;
  /** Field holding the price with tax. */
  grossFieldName: FieldPath<FormType>;
  taxRate: number;
  maxNet: number;
  /** Called after the user edits the price without tax (first or third column). */
  onNetChange: () => void;
  /** Called after the user edits the price with tax. */
  onGrossChange: () => void;
};

/**
 * Three linked price inputs of a single variant. The third column ("pro
 * neplátce") is bound to the very same field as the first one - a provider
 * that is not a VAT payer always charges the price without tax.
 */
export const PriceColumnsRow = <FormType extends FieldValues>(
  props: RowProps<FormType>,
) => {
  const {
    control,
    errors,
    netFieldName,
    grossFieldName,
    taxRate,
    maxNet,
    onNetChange,
    onGrossChange,
  } = props;

  const hasNetError = !!get(errors, netFieldName);
  const hasGrossError = !!get(errors, grossFieldName);

  const commonProps = {
    control,
    errors,
    isNumber: true,
    mask: Number,
    scale: 2,
    min: 0,
    unit: getCurrencySymbol(APP_CURRENCY_CODE),
    formControlClass: "no-padding",
    hasFilterStyle: false,
  } as const;

  return (
    <>
      <StyledPriceCell $hasError={hasNetError}>
        <BlFormInput
          {...commonProps}
          name={netFieldName}
          max={maxNet}
          onKeyUp={() => onNetChange()}
        />
      </StyledPriceCell>

      <StyledPriceCell $hasError={hasGrossError}>
        <BlFormInput
          {...commonProps}
          name={grossFieldName}
          max={Math.ceil(maxNet * (1 + taxRate))}
          onKeyUp={() => onGrossChange()}
        />
      </StyledPriceCell>

      <StyledPriceCell $hasError={hasNetError}>
        <BlFormInput
          {...commonProps}
          name={netFieldName}
          max={maxNet}
          onKeyUp={() => onNetChange()}
        />
      </StyledPriceCell>
    </>
  );
};
